import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";
import { BlockBody, BlockDTO, BlockTargetDTO, CompositeCourseDTO, CourseBlocksDTO, EnrollmentBlocksDTO } from "../../models/CourseBlockDTO";

const RESOURCE_URL_V2 = "/admin/campus/";

/** Cursos compuestos: block catalog per course and block → schedule assignment per enrollment. */
export class CourseBlocksService {

	getBlocks = async (campusId: number, courseId: number): Promise<AxiosResponse<CourseBlocksDTO>> => {
		const uri = `${RESOURCE_URL_V2}${campusId}/courses/${courseId}/_blocks`;
		return axiosInstance.get<CourseBlocksDTO>(uri);
	}

	/** Active modules of the campus a block may point to. Group by courseName in the UI. */
	getTargets = async (campusId: number): Promise<AxiosResponse<BlockTargetDTO[]>> => {
		const uri = `${RESOURCE_URL_V2}${campusId}/courses/_blockTargets`;
		return axiosInstance.get<BlockTargetDTO[]>(uri);
	}

	private targetsCache: { [campusId: number]: Promise<BlockTargetDTO[]> } = {};

	/** `getTargets` cached per campus for the session; a failed request is not cached. */
	getTargetsCached = (campusId: number): Promise<BlockTargetDTO[]> => {
		if (!this.targetsCache[campusId]) {
			this.targetsCache[campusId] = this.getTargets(campusId)
				.then((response) => response.data || [])
				.catch((e) => { delete this.targetsCache[campusId]; throw e; });
		}
		return this.targetsCache[campusId];
	}

	/** Composite courses of the campus, each block with the schedules where it can be started. */
	getCompositeCourses = async (campusId: number): Promise<AxiosResponse<CompositeCourseDTO[]>> => {
		const uri = `${RESOURCE_URL_V2}${campusId}/courses/_composite`;
		return axiosInstance.get<CompositeCourseDTO[]>(uri);
	}

	/** 400/422 `{message}` on invalid data. */
	createBlock = async (campusId: number, courseId: number, body: BlockBody): Promise<AxiosResponse<BlockDTO>> => {
		const uri = `${RESOURCE_URL_V2}${campusId}/courses/${courseId}/_blocks`;
		return axiosInstance.post<BlockDTO>(uri, body);
	}

	updateBlock = async (campusId: number, courseId: number, blockId: number, body: BlockBody): Promise<AxiosResponse<BlockDTO>> => {
		const uri = `${RESOURCE_URL_V2}${campusId}/courses/${courseId}/_blocks/${blockId}`;
		return axiosInstance.put<BlockDTO>(uri, body);
	}

	deleteBlock = async (campusId: number, courseId: number, blockId: number): Promise<AxiosResponse<any>> => {
		const uri = `${RESOURCE_URL_V2}${campusId}/courses/${courseId}/_blocks/${blockId}`;
		return axiosInstance.delete(uri);
	}

	getEnrollmentBlocks = async (campusId: number, studentId: number, enrollmentId: number): Promise<AxiosResponse<EnrollmentBlocksDTO>> => {
		const uri = `${RESOURCE_URL_V2}${campusId}/students/${studentId}/enrollments/${enrollmentId}/_blocks`;
		return axiosInstance.get<EnrollmentBlocksDTO>(uri);
	}

	/** Normal block + scheduleId: MOVES the enrollment to that schedule (no re-billing). "En paralelo" block: authorizes it.
	 *  `scheduleId` null = quitar horario (parallel/optional only). Returns the same payload as `getEnrollmentBlocks`. */
	assignBlock = async (campusId: number, studentId: number, enrollmentId: number, blockId: number, scheduleId: number | null): Promise<AxiosResponse<EnrollmentBlocksDTO>> => {
		const uri = `${RESOURCE_URL_V2}${campusId}/students/${studentId}/enrollments/${enrollmentId}/_blocks/${blockId}`;
		return axiosInstance.put<EnrollmentBlocksDTO>(uri, { scheduleId });
	}
}

/** Standalone instance, like rotation.service: HttpManager is not touched to avoid clashing with parallel features. */
const courseBlocksService = new CourseBlocksService();

export default courseBlocksService;
